/* eslint-disable react-refresh/only-export-components --
   Model module: FieldDefaults binds the default value/display/form renderers
   (DefaultGetValue / DefaultDisplayComponent / DefaultFormItem) defined in this file
   into one field descriptor. Descriptor and renderers are a unit; splitting for a
   dev-HMR benefit fragments the model. */
import { EmptyCellRenderer } from "../../components/cell-renderers/empty-cell-renderer";
import {
  FormItem,
  FormItemOption,
} from "../../components/forms/form-builder.model";

/**
 * Field types supported
 */
/** A qubes data row: named columns of unknown cell type (narrow at the field's getValue). */
export type QubesRecord = Record<string, unknown>;

export type FieldType =
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "enum"
  | "json";

/**
 * Field interface defines the structure and behavior of a field within a data model.
 */
export interface FieldDef {
  /**
   * The name of the field
   */
  name: string;
  /**
   * The label of the field. If not provided, the name will be used.
   */
  label?: string;
  /**
   * The type of the field
   */
  type: FieldType;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Whether the field is editable in the UI
   */
  editable?: boolean;
  /**
   * Whether the field can be null
   */
  nullable?: boolean;
  /**
   * Whether the field is hidden in the UI
   */
  hidden?: boolean;
  /**
   * Explicit column width in pixels for grid display. When set it overrides the
   * data-driven auto-sizing; when omitted the column is sized from its content.
   */
  width?: number;
  /**
   * For enum fields: the allowed {value, text} options that populate the select
   * control. DefaultFormItem maps type "enum" → a "select" FormItem using these.
   */
  enumValues?: FormItemOption[];
  /**
   * Given a record, return the value for this field
   * @param {any} record
   * @returns the value for this field
   */
  getValue: (record: QubesRecord, fieldDef: FieldDef) => unknown;
  /**
   * Given a record, return the value to be used for sorting. If not provided, getValue will be used.
   * @param {any} record
   * @returns the value to be used for sorting
   */
  getSortValue?: (record: QubesRecord, fieldDef: FieldDef) => unknown;
  /**
   * Given a record, return the value to be used for filtering. If not provided, getValue will be used.
   * @param {any} record
   * @returns the value to be used for filtering
   */
  getFilterValue?: (record: QubesRecord, fieldDef: FieldDef) => unknown;
  /**
   * Given a record, return the value to be used for displaying. If not provided, getValue will be used.
   * @param {any} record
   * @returns the value to be used for displaying
   */
  getDisplayValue?: (record: QubesRecord, fieldDef: FieldDef) => string;
  /**
   * Given a record, return a JSX element to be used as the display component for this field
   * @param {any} record
   * @returns JSX.Element
   */
  getDisplayComponent: (record: QubesRecord, fieldDef: FieldDef) => JSX.Element;
  /**
   * Given a record, return a JSX element to be used as an editor for this field
   * @param {any} record
   * @param onChange callback to be called when the value changes
   * @returns JSX.Element
   */
  getFormItem: (record: QubesRecord, fieldDef: FieldDef) => FormItem;
}

/**
 * A numeric field
 */
export interface NumericField extends FieldDef {
  type: "number";

  getValue: (record: QubesRecord, fieldDef: FieldDef) => number;
}

/**
 * A string field
 */
export interface StringField extends FieldDef {
  type: "string";

  getValue: (record: QubesRecord, fieldDef: FieldDef) => string;
}

/**
 * A boolean field
 */
export interface BooleanField extends FieldDef {
  type: "boolean";

  getValue: (record: QubesRecord, fieldDef: FieldDef) => boolean;
}

/**
 * A date field
 */
export interface DateField extends FieldDef {
  type: "date";

  getValue: (record: QubesRecord, fieldDef: FieldDef) => Date;
}

/**
 * An enum field
 */
export interface EnumField extends FieldDef {
  type: "enum";

  // enums are represented as strings (keys) in most cases
  getValue: (record: QubesRecord, fieldDef: FieldDef) => string;
}

export const DefaultGetValue = (record: QubesRecord, fieldDef: FieldDef) =>
  record[fieldDef.name];

export const DefaultDisplayComponent = (record: QubesRecord, fieldDef: FieldDef) => {
  // Display what the field's getValue yields, so a computed field (an Owner
  // badge, yes/no for a boolean, a "—" for an absent value) shows its value
  // rather than the raw column. The default getValue is the raw column.
  const value = fieldDef.getValue ? fieldDef.getValue(record, fieldDef) : record[fieldDef.name];
  if (value === null || value === undefined) {
    return <EmptyCellRenderer />;
  } else {
    return <span>{String(value)}</span>;
  }
};

export const DefaultFormItem = (record_: QubesRecord, field: FieldDef): FormItem => {
  const base = {
    name: field.name,
    label: field.label,
    disabled: field.editable === false,
    required: field.required,
  };
  switch (field.type) {
    case "number":
      return { ...base, type: "number" };
    case "boolean":
      return { ...base, type: "toggle" };
    case "json":
      return { ...base, type: "json" };
    case "enum":
      // enum → select, populated from the field's declared allowed values
      return { ...base, type: "select", options: field.enumValues ?? [] };
    case "date": // TODO: dedicated date-picker form-item type
    case "string":
    default:
      return { ...base, type: "text" };
  }
};

export const FieldDefaults = {
  type: "string" as FieldType,
  getValue: DefaultGetValue,
  getFormItem: DefaultFormItem,
  getDisplayComponent: DefaultDisplayComponent,
};
